/**
 * Which instances a series edit would reach, mirrored on the client so the scope
 * chooser can recount as the reader switches option without a round trip.
 *
 * <p><b>The server decides, not this.</b> `TaskSetEligibility` in service-company
 * is the rule; this restates it only to show counts, and the Apply that follows
 * is re-evaluated there. If the two ever disagree the server wins, and the fix
 * belongs here rather than in a client-side override. Change one, change both.
 */

/** The field names the server uses. Two vocabularies for the same set would drift. */
export const FORWARD_ONLY_FIELDS = ['phase', 'role', 'assigned', 'billable'] as const;

export interface InstanceState {
	readonly sequence?: number | null;
	readonly companyTaskId?: number | null;
	readonly eligible?: boolean | null;
	readonly worked?: boolean | null;
	readonly invoiced?: boolean | null;
	readonly hasActuals?: boolean | null;
}

const isForwardOnly = (field: string): boolean => (FORWARD_ONLY_FIELDS as readonly string[]).includes(field);

/**
 * Whether the scope can narrow anything at all.
 *
 * <p>Only forward-only fields answer to it. A rename or a description reaches
 * every eligible instance whatever boundary is chosen, so offering "from
 * instance N forward" for one of those presents an option that does exactly what
 * the other does while claiming to leave earlier instances alone.
 */
export const hasForwardOnlyChange = (changedFields: ReadonlyArray<string>): boolean => changedFields.some(isForwardOnly);

/** Whether a change to `field` reaches this instance, ignoring scope. */
export const reaches = (field: string, instance: InstanceState): boolean => {
	if (!instance.eligible) return false;
	if (isForwardOnly(field)) {
		// Never a worked or invoiced instance, even an otherwise eligible one:
		// moving its phase, role, assignee or billability would move actuals,
		// budget or rollups already recorded against it.
		return !instance.worked && !instance.invoiced;
	}
	/* Estimate has no tier of its own. It read as "no actuals", which the
	   eligibility gate above already guarantees -- the same answer written twice.
	   Reaching a hand-edited instance, which is what the old comment described,
	   would contradict the rule that divergence protects it. */
	return true;
};

/**
 * The natural forward-only boundary: the first position from which nothing has
 * been worked or invoiced.
 *
 * <p>This is what "From instance N forward" offers. Picking any earlier one would
 * present an option that reaches the same instances as the other, and picking a
 * later one would leave clean instances behind for no reason.
 *
 * <p>Null when there is no protected instance at all, which is the untouched set
 * -- and then there is no question to ask.
 */
export const forwardBoundary = (instances: ReadonlyArray<InstanceState>): number | null => {
	let lastProtected: number | null = null;
	instances.forEach(instance => {
		const sequence = instance.sequence ?? null;
		if (sequence === null) return;
		if (!instance.eligible || instance.worked || instance.invoiced) {
			lastProtected = lastProtected === null || sequence > lastProtected ? sequence : lastProtected;
		}
	});
	return lastProtected === null ? null : lastProtected + 1;
};

/**
 * How many instances the changed fields would reach at this scope.
 *
 * <p>An instance counts once, however many fields reach it -- the number the
 * reader is shown is how many tasks change, not how many writes happen.
 */
export const countReached = (
	instances: ReadonlyArray<InstanceState>,
	changedFields: ReadonlyArray<string>,
	fromSequence: number | null
): number =>
	instances.filter(instance => {
		const inScope = fromSequence === null || (instance.sequence ?? -1) >= fromSequence;
		return changedFields.some(field => {
			// The scope narrows forward-only fields; it never holds back a rename.
			if (isForwardOnly(field) && !inScope) return false;
			return reaches(field, instance);
		});
	}).length;
